<template>
	<div class="accordion sticky-top" id="equity-request">
		<div v-if="error" class="alert alert-warning alert-dismissible fade show shadow-sm" :style="'width: -webkit-fill-available'" role="alert">
			<div v-html="error"></div>
			<button type="button" class="close" data-dismiss="alert" aria-label="Close">
				<span aria-hidden="true">×</span>
			</button>
		</div>

		<form @submit.prevent="validateEquitiesRequest" class="card shadow-sm mg-b-40">
			<div class="card-header pd-t-20 d-sm-flex align-items-start justify-content-between bd-b-0 pd-b-0">
				<div>
					<h6 class="mg-b-5">
						<button class="btn btn-link" type="button" data-toggle="collapse" data-target="#collapseEquityRequest" aria-expanded="true" aria-controls="collapseEquityRequest">
							Equities Transaction Request
						</button>
					</h6>
					<p class="tx-13 tx-color-03 mg-b-0"></p>
				</div>
			</div>
			<div id="collapseEquityRequest" class="collapse show" data-parent="#equity-request">
				<div class="card-body equity-request pd-y-30">
					<div class="table-responsive">
						<table class="table table-dashboard mg-b-0" aria-describedby="equity-request-table">
							<thead>
								<tr>
									<th scope="col" class="tx-medium text-center">Type</th>
									<th scope="col" class="tx-medium text-center">Ticker</th>
									<th scope="col">Volume</th>
									<th scope="col">Order Type</th>
									<th scope="col">Price</th>
									<th scope="col" class="tx-medium text-center">Charges</th>
									<th scope="col" class="tx-medium text-center">Est Consideration</th>
									<th scope="col" class="tx-medium text-center"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(request, index) in form.trade_data" :key="index">
									<td class="tx-medium text-center" :class="request.type">
										<select
											v-model="request.type"
											class="form-control form-control-sm shadow-sm"
											:class="{ 'is-invalid': form.errors.has(`trade_data.${index}.type`) }"
											@change="checkTradingType($event, index)"
											style="width: auto"
										>
											<option selected :value="null" disabled>Select an option</option>
											<option v-for="(option, index) in tradeTypes" :disabled="option.value !== 'BUY' ? !request.equity.holding : false" :value="option.value" :key="index">
												{{ option.name | toStartCase }}
											</option>
										</select>
									</td>
									<td class="tx-color-03 tx-medium text-center">
										{{ request.equity.symbol }}
									</td>
									<td>
										<div class="form-group mb-0">
											<input
												@change="checkPrice(request)"
												type="number"
												step="any"
												v-model="request.volume"
												class="form-control form-control-sm shadow-sm"
												:class="{
													'is-invalid': form.errors.has(`trade_data.${index}.volume`),
												}"
												min="0.0"
												placeholder="Enter number of shares"
												required
												style="width: auto"
											/>
											<has-error :form="form" :field="`trade_data.${index}.volume`"></has-error>
										</div>
									</td>
									<td>
										<div class="form-group mb-0">
											<select
												v-model="request.order_type"
												class="form-control form-control-sm shadow-sm"
												@change="checkPrice(request)"
												:class="{
													'is-invalid': form.errors.has(`trade_data.${index}.order_type`),
												}"
											>
												<option selected :value="null" disabled>Select an option</option>
												<option v-for="(option, index) in orderTypes.localMarket" :value="option.value" :key="index">
													{{ option.name | toStartCase }}
												</option>
											</select>
											<has-error :form="form" :class="'d-flex'" :field="`trade_data.${index}.order_type`"></has-error>
										</div>
									</td>
									<td>
										<div class="form-group mb-0">
											<input
												type="number"
												step="any"
												v-model="request.price"
												:min="request.equity_price_band.lower_price"
												:max="request.equity_price_band.upper_price"
												class="form-control form-control-sm shadow-sm"
												:class="{
													'is-invalid': form.errors.has(`trade_data.${index}.price`),
												}"
												:readonly="request.order_type === 'MARKET_ORDER'"
											/>
											<small v-if="request.order_type === 'LIMIT_ORDER'" class="form-text text-muted"
												>Price must be between
												{{ request.equity_price_band.lower_price }}
												and
												{{ request.equity_price_band.upper_price }}</small
											>
											<has-error :form="form" :field="`trade_data.${index}.price`"></has-error>
										</div>
									</td>
									<td class="tx-medium text-center">
										{{ request.charges | toCurrency(request.currency) }}
									</td>
									<td class="tx-medium text-center">
										{{ request.net_consideration | toCurrency(request.currency) }}
									</td>
									<td class="tx-medium text-center">
										<button v-if="request.type === 'Sell'" type="button" class="btn btn-sm btn-warning shadow-sm" @click="sellAllHoldings(index, request)">Sell All</button>
										<button type="button" class="btn btn-sm btn-danger shadow-sm" @click="removeEquity(request.equity.stock_code, index)">Remove</button>
									</td>
								</tr>
								<tr v-if="false">
									<td class="tx-medium Total" colspan="4">Total</td>
									<td class="tx-medium text-center Total">
										{{ form.trade_cost.charges | toCurrency('GHS') }}
									</td>
									<td class="tx-medium text-center Total">
										{{ form.trade_cost.net_consideration | toCurrency('GHS') }}
									</td>
									<td></td>
								</tr>
								<tr v-if="trade.validation.message">
									<td class="tx-medium tx-danger" colspan="7" v-html="trade.validation.message"></td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
			<div class="card-footer d-flex justify-content-end">
				<button-component :busy="form.busy" :label="'Proceed'" :label-loading="'Please Wait...'" :button-class="'btn btn-default shadow-sm mr-1'"></button-component>
				<button type="button" class="btn btn-primary shadow-sm ml-1" @click="removeAllOrders">Cancel</button>
			</div>
		</form>

		<div :class="loadingCounter ? 'loading-overlay' : 'd-none'">
			<spinner-component :loading="true" :classes="'spinner-border-sm'" />
			<p class="my-0 ml-1 d-inline">Please Wait...</p>
		</div>
	</div>
</template>

<style>
@media (min-width: 992px) {
	#equity-request {
		top: 103px;
	}
}

.loading-overlay {
	position: absolute;
	top: 0;
	right: 0;
	height: 100%;
	width: -moz-available; /* WebKit-based browsers will ignore this. */
	width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
	width: stretch;
	padding: 1em;
	background-color: rgb(234 234 234 / 30%);
	display: flex;
	justify-content: center;
	align-items: center;
}
</style>

<script>
import { mapGetters } from 'vuex';
import { trade } from '../../../../../mixins';
import TradeSummaryComponent from './TradeSummaryComponent.vue';

export default {
	components: { TradeSummaryComponent },
	mixins: [trade.equities],
	data() {
		return {
			error: null,
			loading: this.loadingCounter,
			trade: {
				validation: {
					message: null,
				},
			},
		};
	},
	computed: {
		...mapGetters({
			orderTypes: 'trade/orderTypes',
			tradeTypes: 'trade/tradeTypes',
			user: 'user',
			company: 'company',
		}),
	},
	methods: {
		calculateCost(request, params) {
			this.$emit('calculate-trade-cost', { request, params });
		},
		checkPrice(request) {
			this.trade.validation.message = null;
			request.price = request.order_type === 'MARKET_ORDER' ? request.basePrice : request.price;

			const {
				volume,
				price,
				type,
				equity: { symbol, stock_code },
				order_type,
				order_source,
				volume_threshold,
			} = request;

			if (volume) {
				this.$emit('update-counter-status', true);

				axios
					.post('/api/trade/order-action/validate', {
						account_code: this.company.account_code,
						stock_code,
						symbol,
						price,
						volume,
						type: _.upperCase(type),
						auto_target: true,
						order_source,
						order_type,
					})
					.then(
						({
							data: {
								data: { validate },
							},
						}) => {
							const { charges, consideration, net_consideration, price_change: equity_price_band, valid_order, message } = validate;

							request.volume_status = parseFloat(volume) <= parseFloat(volume_threshold);
							request.is_valid_order = valid_order;

							if (valid_order) {
								this.calculateCost(request, {
									charges,
									consideration,
									net_consideration,
									equity_price_band,
								});
							}
						}
					)
					.catch(
						({
							response: {
								data: {
									data: { validate },
								},
							},
						}) => {
							this.trade.validation.message = validate;
						}
					)
					.finally(() => this.$emit('update-counter-status', false));
			}
		},
		checkTradingType(event, index) {},
		removeEquity(stock, index) {
			this.$emit('remove-equity', { stock, index });
		},
		removeAllOrders() {
			this.$emit('remove-all');
		},
		validateEquitiesRequest() {
			this.$emit('set-action', 'validate');
		},
	},
	props: {
		form: {
			required: true,
		},
		loadingCounter: {
			type: Boolean,
			required: false,
		},
	},
};
</script>

<style>
</style>